import { MetricKind, type MetricDefinition } from "@gen/tracker/v1/reporting_pb";

import { evaluateFormulas, type ReportingSchema } from "@/lib/services/reporting-schema-service";
import type { TKey } from "@/lib/i18n";

/**
 * The column catalog and the bag arithmetic the report engine and the cards
 * share — pure, so the arithmetic that decides a number on the screen is the
 * arithmetic the tests pin.
 *
 * ONE DEFINITION OF EVERY NUMBER. A bag is the server's metric catalog for a
 * row. Nothing else is computed here except what доска Metrics itself defines
 * from two of those numbers (боты % = боты / визиты). A sum of rows SUMS the
 * base measures and re-derives every rate through the catalog's own formulas —
 * an approve rate averaged over rows is not the workspace's approve rate, and
 * a summed ROI is not a number at all.
 *
 * When the registry grows a key the catalog ships wins over the local
 * stop-gap, and a column that waited for it lights up without a change here.
 */

export type Band = "traffic" | "conversions" | "money";
export type EditorGroup = "traffic" | "goals" | "conversions" | "money";
export type Format = "int" | "pct1" | "pct0" | "money" | "unit" | "profit" | "roi" | "outcome";

export interface ColumnSpec {
  /** Column id, and the key in the row's bag. */
  key: string;
  label: TKey | string;
  /** The table head's word when the column is narrow (доска Main: «Боты» over a percent). */
  short?: TKey;
  band: Band;
  group: EditorGroup;
  format: Format;
  /** Muted ink: a rate beside the counts it is made of. */
  dim?: boolean;
  /** Not in the registry yet — the editor marks it «новое» and keeps it off until the catalog ships it. */
  needsCatalog?: boolean;
  /** Derived here from catalog keys until the catalog ships the key itself. */
  local?: boolean;
}

/** Every column the editor offers, in the order доска Columns lists them. Goals come from the catalog. */
export const COLUMNS: ColumnSpec[] = [
  { key: "visits", label: "cols.visits", band: "traffic", group: "traffic", format: "int" },
  { key: "clicks", label: "cols.clicks", band: "traffic", group: "traffic", format: "int" },
  { key: "unique_clicks", label: "cols.uniques", band: "traffic", group: "traffic", format: "int" },
  { key: "bots", label: "cols.bots", band: "traffic", group: "traffic", format: "int" },
  { key: "bots_pct", label: "cols.botsPct", short: "cols.bots", band: "traffic", group: "traffic", format: "pct1", dim: true, local: true },
  { key: "lander_shows", label: "cols.lpShows", band: "traffic", group: "traffic", format: "int" },
  // The registry's cta_clicks are the LP clicks (a click on the lander's button); offer_visits are
  // «Переходы на оффер» — visits that reached an offer, from a button or straight from the flow.
  { key: "cta_clicks", label: "cols.lpClicks", band: "traffic", group: "traffic", format: "int" },
  { key: "lp_ctr", label: "cols.lpCtr", band: "traffic", group: "traffic", format: "pct0", dim: true, needsCatalog: true },
  { key: "offer_visits", label: "cols.toOffer", short: "cols.toOfferShort", band: "traffic", group: "traffic", format: "int" },
  { key: "leads", label: "cols.leads", band: "conversions", group: "conversions", format: "int" },
  { key: "conversions", label: "cols.approved", band: "conversions", group: "conversions", format: "int" },
  { key: "conversions_hold", label: "cols.hold", band: "conversions", group: "conversions", format: "int" },
  { key: "rejected", label: "cols.rejected", band: "conversions", group: "conversions", format: "int" },
  { key: "trash", label: "cols.trash", band: "conversions", group: "conversions", format: "int" },
  { key: "cr", label: "cols.cr", band: "conversions", group: "conversions", format: "pct1", dim: true },
  { key: "outcome", label: "cols.outcome", band: "conversions", group: "conversions", format: "outcome" },
  { key: "approve_rate", label: "cols.approveRate", band: "conversions", group: "conversions", format: "pct0" },
  { key: "revenue", label: "cols.revenue", band: "money", group: "money", format: "money" },
  { key: "revenue_hold", label: "cols.revenueHold", short: "kpi.hold", band: "money", group: "money", format: "money" },
  { key: "cost", label: "cols.cost", band: "money", group: "money", format: "money" },
  { key: "profit", label: "cols.profit", band: "money", group: "money", format: "profit" },
  { key: "roi", label: "cols.roi", band: "money", group: "money", format: "roi" },
  { key: "epc", label: "cols.epc", band: "money", group: "money", format: "unit", dim: true },
  { key: "cpc", label: "cols.cpc", band: "money", group: "money", format: "unit", dim: true, needsCatalog: true },
  { key: "cpl", label: "cols.cpl", band: "money", group: "money", format: "unit", dim: true, needsCatalog: true },
  { key: "cpa", label: "cols.cpa", band: "money", group: "money", format: "unit", dim: true },
  { key: "roas", label: "cols.roas", band: "money", group: "money", format: "pct0", needsCatalog: true },
];

export const BAND_ORDER: Band[] = ["traffic", "conversions", "money"];

export type ColumnSetId = "money" | "funnel" | "quality" | "compare" | "mine";
export const COLUMN_SETS: ColumnSetId[] = ["money", "funnel", "quality", "compare", "mine"];

/** The built-in sets of доска Columns. «Мои» is the reader's own, kept in the URL. */
export const PRESET_SETS: Record<Exclude<ColumnSetId, "mine">, string[]> = {
  money: ["clicks", "bots_pct", "lp_ctr", "leads", "cr", "outcome", "approve_rate", "cost", "revenue", "cpa", "profit", "roi"],
  funnel: ["visits", "clicks", "lander_shows", "cta_clicks", "lp_ctr", "leads", "conversions", "cr", "approve_rate"],
  quality: ["visits", "clicks", "bots", "bots_pct", "unique_clicks", "leads", "cr", "approve_rate"],
  compare: ["clicks", "leads", "cr", "cost", "revenue", "profit", "roi"],
};

export function parseSet(v: string | null | undefined): ColumnSetId {
  return (COLUMN_SETS as string[]).includes(v ?? "") ? (v as ColumnSetId) : "money";
}

/** Goal counts from the catalog — the editor's «По целям». */
export function goalColumns(schema: ReportingSchema | undefined): ColumnSpec[] {
  if (!schema) return [];
  return schema.metrics
    .filter((m) => m.goalKey && m.key.endsWith(".count"))
    .map((m) => ({ key: m.key, label: m.label, band: "conversions", group: "goals", format: "int" }) as ColumnSpec);
}

export function allColumns(schema: ReportingSchema | undefined): ColumnSpec[] {
  const goals = goalColumns(schema);
  const at = COLUMNS.findIndex((c) => c.band === "conversions");
  return [...COLUMNS.slice(0, at), ...goals, ...COLUMNS.slice(at)];
}

/** A column has numbers behind it: local, the outcome bar, or a key the catalog ships. */
export function isAvailable(spec: ColumnSpec, schema: ReportingSchema | undefined): boolean {
  if (spec.local || spec.format === "outcome") return true;
  return !!schema?.metricsByKey.has(spec.key);
}

// --- rows --------------------------------------------------------------------

export type Bag = Record<string, number>;

/** The keys a group row and the totals SUM; everything else is re-derived. */
export function baseKeys(schema: ReportingSchema): string[] {
  return schema.metrics.filter((m) => m.kind !== MetricKind.DERIVED).map((m) => m.key);
}

/**
 * Re-derive what is derived: the catalog's formulas, then the local bots %
 * (доска Metrics: «визиты, признанные ботом / визиты») when the catalog does
 * not ship it. Pure.
 */
export function derive(metrics: MetricDefinition[], base: Bag): Bag {
  const out = evaluateFormulas(metrics, base);
  if (!metrics.some((m) => m.key === "bots_pct")) {
    if ((out.visits ?? 0) > 0) out.bots_pct = (out.bots ?? 0) / out.visits!;
    else delete out.bots_pct;
  }
  return out;
}

/** Sum the base measures of `bags` and re-derive. Pure. */
export function aggregate(bags: Bag[], schema: ReportingSchema): Bag {
  const keys = baseKeys(schema);
  const sum: Bag = {};
  for (const k of keys) {
    let acc = 0;
    let seen = false;
    for (const b of bags) {
      const x = b[k];
      if (x !== undefined) {
        acc += x;
        seen = true;
      }
    }
    if (seen) sum[k] = acc;
  }
  return derive(schema.metrics, sum);
}
